import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-mist focus:border-brand outline-none text-ink placeholder:text-ink/40 px-4 py-3.5 text-sm rounded-lg transition-colors";
const selectCls =
  "w-full bg-white border border-mist focus:border-brand text-ink data-[placeholder]:text-ink/40 px-4 h-[50px] text-sm rounded-lg";

const SelectField = ({ testid, placeholder, value, onChange, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className={selectCls}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="bg-white border-mist text-ink rounded-lg">
      {options.map((o) => (
        <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const emptyForm = {
  full_name: "", email: "", phone: "", company: "",
  net_worth_bracket: "", jurisdiction: "", service_interest: "",
  preferred_contact_time: "", message: "",
};

export default function LeadFormWizard() {
  const { t, lang } = useLanguage();
  const f = t.form;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));

  const next = () => {
    if (step === 0 && (!form.service_interest || !form.jurisdiction || !form.net_worth_bracket)) {
      toast.error(f.validation); return;
    }
    if (step === 1) {
      if (!form.full_name || !form.email) { toast.error(f.validation); return; }
      if (!/\S+@\S+\.\S+/.test(form.email)) { toast.error(f.invalidEmail); return; }
    }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, language: lang });
      toast.success(f.success); setForm(emptyForm); setStep(0);
    } catch {
      toast.error(f.error);
    } finally { setLoading(false); }
  };

  const label = (options, v) => options.find((o) => o.v === v)?.l || v;
  const summary = [
    [f.labels.service.replace(" *", ""), label(f.options.services, form.service_interest)],
    [f.labels.jurisdiction.replace(" *", ""), label(f.options.jurisdictions, form.jurisdiction)],
    [f.labels.networth.replace(" *", ""), label(f.options.networth, form.net_worth_bracket)],
    [f.labels.name.replace(" *", ""), form.full_name],
    [f.labels.email.replace(" *", ""), form.email],
  ];

  return (
    <section id="consultation" data-testid="lead-form-section" className="bg-paper py-24 md:py-32 border-t border-mist">
      <div className="max-w-[1360px] mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20">
          <Reveal className="md:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-6">{f.overline}</p>
            <h2 className="font-display font-bold text-4xl md:text-5xl text-ink leading-tight tracking-tight">{f.title}</h2>
            <p className="mt-6 text-base text-ink/60 leading-relaxed">{f.desc}</p>
            <div className="mt-10 space-y-3 text-sm text-ink/60">
              <p data-testid="contact-email">{f.email}</p>
              <p data-testid="contact-phone">{f.phone}</p>
            </div>
          </Reveal>
          <Reveal className="md:col-span-7" delay={0.15}>
            <div className="bg-white border border-mist rounded-2xl p-6 md:p-8">
              <div className="flex items-center gap-0 mb-8 border border-mist rounded-lg overflow-hidden">
                {f.steps.map((s, i) => (
                  <div
                    key={s}
                    data-testid={`wizard-step-${i}`}
                    className={`flex-1 flex items-center gap-3 px-4 py-3.5 ${i < 2 ? "border-e border-mist" : ""} ${i === step ? "bg-brand-50" : ""}`}
                  >
                    <span className={`w-6 h-6 flex items-center justify-center text-xs shrink-0 rounded-full ${i < step ? "bg-brand text-white" : i === step ? "border border-brand text-brand" : "border border-mist text-ink/40"}`}>
                      {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </span>
                    <span className={`text-xs uppercase tracking-widest hidden sm:block ${i === step ? "text-ink" : "text-ink/40"}`}>{s}</span>
                  </div>
                ))}
              </div>
              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.35, ease: "easeOut" }}>
                  {step === 0 && (
                    <div className="grid grid-cols-1 gap-5">
                      <SelectField testid="lead-select-service" placeholder={f.labels.service} value={form.service_interest} onChange={set("service_interest")} options={f.options.services} />
                      <SelectField testid="lead-select-jurisdiction" placeholder={f.labels.jurisdiction} value={form.jurisdiction} onChange={set("jurisdiction")} options={f.options.jurisdictions} />
                      <SelectField testid="lead-select-networth" placeholder={f.labels.networth} value={form.net_worth_bracket} onChange={set("net_worth_bracket")} options={f.options.networth} />
                    </div>
                  )}
                  {step === 1 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <input data-testid="lead-input-name" className={inputCls} placeholder={f.labels.name} value={form.full_name} onChange={(e) => set("full_name")(e.target.value)} />
                      <input data-testid="lead-input-email" className={inputCls} type="email" placeholder={f.labels.email} value={form.email} onChange={(e) => set("email")(e.target.value)} />
                      <input data-testid="lead-input-phone" className={inputCls} placeholder={f.labels.phone} value={form.phone} onChange={(e) => set("phone")(e.target.value)} />
                      <input data-testid="lead-input-company" className={inputCls} placeholder={f.labels.company} value={form.company} onChange={(e) => set("company")(e.target.value)} />
                      <div className="sm:col-span-2">
                        <SelectField testid="lead-select-contact-time" placeholder={f.labels.contactTime} value={form.preferred_contact_time} onChange={set("preferred_contact_time")} options={f.options.contactTimes} />
                      </div>
                    </div>
                  )}
                  {step === 2 && (
                    <div className="grid grid-cols-1 gap-5">
                      <textarea data-testid="lead-input-message" className={`${inputCls} min-h-[110px] resize-none`} placeholder={f.labels.message} value={form.message} onChange={(e) => set("message")(e.target.value)} />
                      <div data-testid="wizard-summary" className="border border-mist rounded-lg p-5 bg-paper">
                        <p className="text-xs uppercase tracking-widest text-brand mb-4">{f.reviewTitle}</p>
                        <dl className="space-y-2">
                          {summary.map(([k, v]) => (
                            <div key={k} className="flex justify-between gap-6 text-sm">
                              <dt className="text-ink/50">{k}</dt>
                              <dd className="text-ink font-medium text-end">{v}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
              <div className="flex gap-4 mt-8">
                {step > 0 && (
                  <button data-testid="wizard-back-button" onClick={() => setStep((s) => s - 1)}
                    className="border border-mist text-ink hover:bg-paper transition-colors px-8 py-4 text-sm font-semibold rounded-full">
                    {f.back}
                  </button>
                )}
                {step < 2 ? (
                  <button data-testid="wizard-next-button" onClick={next}
                    className="flex-1 bg-brand hover:bg-brand-700 transition-colors text-white px-8 py-4 text-sm font-semibold rounded-full">
                    {f.next}
                  </button>
                ) : (
                  <button data-testid="lead-submit-button" onClick={submit} disabled={loading}
                    className="flex-1 bg-brand hover:bg-brand-700 disabled:opacity-60 transition-colors text-white px-8 py-4 text-sm font-semibold rounded-full">
                    {loading ? f.submitting : f.submit}
                  </button>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
